import {
  createListenerMiddleware,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import {
  getBMCWithRatingApi,
  sendRequestToBMCApi,
} from "src/api/bmcWithRatingAPI";
import {
  IBMCWithRating,
  IBMCWithRatingData,
} from "../interfaces/IBMCWithRating";
import { NUMBER_OF_ROWS_PER_PAGE } from "src/resources/constant";
import { getTextValueById, toCamel, toPascal } from "src/utils/helpers";
import { trackPromise } from "react-promise-tracker";

export const getBMCWithRating = (forExport = false) => {
  return async (dispatch: any, getState: any) => {
    const {
      bmcWithRating,
      lookup: { serviceTerritories },
    } = getState();

    const { filterData, gridCurrentPage, numberOfRowsPerPage } = bmcWithRating;

    const currentPage = gridCurrentPage + 1;
    const selectedCompanyIds = [];

    if (filterData.company.length > 0) {
      for (const index in filterData.company) {
        selectedCompanyIds.push(filterData.company[index]);
      }
    }
    const allCityTerritories = serviceTerritories.map(
      (item: any) => item.territoryId
    );

    let territories = filterData.territory;
    if (filterData.city && (!territories || territories.length === 0)) {
      territories = allCityTerritories;
    }
    const data = {
      ...toPascal({ ...filterData, company: selectedCompanyIds }),
      Territory: territories,
      PageNumber: currentPage,
      PageCount: numberOfRowsPerPage,
    };
    if (forExport) {
      data.PageNumber = 1;
      data.PageCount = numberOfRowsPerPage * currentPage;
    }

    await trackPromise(
      getBMCWithRatingApi(data).then((response) => {
        const data = toCamel(response?.data?.BMCs || []);
        const totalRecords = response.data?.TotalRowsCount || 0;
        if (forExport) {
          dispatch(setExportData(data));
        } else {
          dispatch(setBMCWithRating({ data, totalRecords }));
        }
      })
    );
  };
};

export const sendRequestToBMC = () => {
  return async (dispatch: any, getState: any) => {
    const { bmcWithRating } = getState();
    const data = bmcWithRating.sendRequestToBMCPopupData;
    trackPromise(
      sendRequestToBMCApi(data).then((response) => {
        dispatch(setIsSendRequestToBMCPopupOpen(false));
        dispatch(clearSendRequestToBMCPopupData());
      })
    );
  };
};

const initialState: IBMCWithRating = {
  bmcWithRatingData: [],
  bmcWithRatingDetailsPopupData: {
    companyName: "",
    companyRating: "",
    paymentStatus: "",
    tradeLicenseNumber: "",
    tradeLicenseExpirationDate: new Date(),
    civilDefenseLicenseNumber: "",
    civilDefenseLicenseExpirationDate: new Date(),
    latitude: 0,
    longitude: 0,
    territory: "",
    emirate: "",
    officePhoneNumber: "",
    contactPersonName: "",
    designation: "",
    mobile: "",
    numberOfTechnicians: 0,
    numberOfElectricalEngineers: 0,
    numberOfMechanicalEngineers: 0,
    numberOfElectricalTechnicians: 0,
    numberOfCertifiedEngineers: 0,
    numberOfMechanicalTechnicians: 0,
    id: "",
  },
  sendRequestToBMCPopupData: {
    bmcId: "",

    templateId: 0,
    subject: "",
    body: "",
  },
  filterData: {
    company: [],
    rating: undefined,
    territory: undefined,
    PaymentStatus: undefined,
    city: "",
  },
  isBMCWithRatingDetailsPopupOpen: false,
  isSendRequestToBMCPopupOpen: false,
  totalRowsCount: 0,
  gridCurrentPage: 0,
  numberOfRowsPerPage: NUMBER_OF_ROWS_PER_PAGE,
  exportData: [],
};

const Slice = createSlice({
  name: "bmcWithRating",
  initialState,
  reducers: {
    setBMCWithRating: (state, action) => {
      state.bmcWithRatingData = action.payload.data;
      state.totalRowsCount = action.payload.totalRecords;
    },

    setIsBMCWithRatingDetailsPopupOpen: (state, action) => {
      state.isBMCWithRatingDetailsPopupOpen = action.payload;
    },
    setIsSendRequestToBMCPopupOpen: (state, action) => {
      state.isSendRequestToBMCPopupOpen = action.payload;
    },
    setBMCWithRatingPopupData: (
      state,
      action: {
        payload: { field: string; value: string | number };
        type: string;
      }
    ): void => {
      state.bmcWithRatingDetailsPopupData = {
        ...state.bmcWithRatingDetailsPopupData,
        [action.payload.field]: action.payload.value,
      };
    },
    setSendRequestToBMCPopupData: (state, action) => {
      state.sendRequestToBMCPopupData = action.payload;
    },
    clearBMCWithRatingPopupData: (state) => {
      state.bmcWithRatingDetailsPopupData =
        initialState.bmcWithRatingDetailsPopupData;
    },
    clearSendRequestToBMCPopupData: (state) => {
      state.sendRequestToBMCPopupData = initialState.sendRequestToBMCPopupData;
    },

    setFilterDataField: (state, action) => {
      state.filterData = {
        ...state.filterData,
        [action.payload.field]: action.payload.value,
      };
    },
    resetFilterData: (state) => {
      state.filterData = initialState.filterData;
      state.gridCurrentPage = initialState.gridCurrentPage;
    },
    setBMCWithRatingGridCurrentPage: (state, action) => {
      state.gridCurrentPage = action.payload;
    },
    setNumberOfRowsPerPage: (state, action) => {
      state.numberOfRowsPerPage = action.payload;
    },
    setExportData: (state, action) => {
      state.exportData = action.payload;
    },
  },
});

export const {
  setBMCWithRating,
  setIsBMCWithRatingDetailsPopupOpen,
  setIsSendRequestToBMCPopupOpen,
  setBMCWithRatingPopupData,
  setSendRequestToBMCPopupData,
  clearBMCWithRatingPopupData,
  clearSendRequestToBMCPopupData,
  setFilterDataField,
  resetFilterData,
  setBMCWithRatingGridCurrentPage,
  setNumberOfRowsPerPage,
  setExportData,
} = Slice.actions;

export default Slice.reducer;

const makeFilterBMCWithRatingSelector = (
  dataSourceSelector: (state: any) => any
) =>
  createSelector(
    dataSourceSelector,
    (state: any) => state.lookup.statusOptions,
    (state: any) => state.lookup.onHoldReasons,
    (bmcWithRatingData, paymentStatus) => {
      return getBMCWithRatingFilteredData(bmcWithRatingData, paymentStatus);
    }
  );

export const selectBMCWithRating = makeFilterBMCWithRatingSelector(
  (state: any) => state.bmcWithRating.bmcWithRatingData
);
export const selectBMCWithRatingForExport = makeFilterBMCWithRatingSelector(
  (state: any) => state.bmcWithRating.exportData
);

const getBMCWithRatingListenerMiddleware = createListenerMiddleware();

getBMCWithRatingListenerMiddleware.startListening({
  actionCreator: setBMCWithRatingGridCurrentPage,
  effect: async (action, listenerApi) => {
    listenerApi.dispatch(getBMCWithRating());
  },
});

export { getBMCWithRatingListenerMiddleware };
function getBMCWithRatingFilteredData(
  bmcWithRatingData: any,
  paymentStatus: any
): any {
  if (!bmcWithRatingData) return [];
  return bmcWithRatingData?.filter((bmcWithRating: IBMCWithRatingData) => {
    return Object.values(bmcWithRating).some((field) => {
      return field?.toString()?.toLowerCase();
    });
  });
}
